import { memo } from 'react'
import { Search, Plus, ChevronLeft, ChevronRight, Receipt, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { TableSkeleton } from '@/components/loading'
import { EmptyState } from '@/components/shared'
import { AddTransactionDialog, EditTransactionDialog } from '@/components/features/financials'
import type { FinancialTransaction } from '@/types'
import { useTransactionsPage } from './TransactionsPage.logic'
import { formatAmountCents, typeBadgeVariant } from './TransactionsPage.static'

const TRANSACTION_TYPE_KEYS = ['INCOME', 'EXPENSE'] as const

const CATEGORY_KEYS = [
  'PATIENT_FEE',
  'SALARY',
  'UTILITIES',
  'SUPPLIES',
  'MAINTENANCE',
  'GOVERNMENT_FUNDING',
  'OTHER',
] as const

export function TransactionsPage() {
  const {
    t,
    transactions,
    total,
    page,
    totalPages,
    search,
    typeFilter,
    categoryFilter,
    addDialogOpen,
    deleteTargetId,
    editTargetTransaction,
    isLoading,
    isEmpty,
    setSearch,
    setPage,
    setAddDialogOpen,
    setDeleteTargetId,
    setEditTargetTransaction,
    handleTypeFilterChange,
    handleCategoryFilterChange,
    handleDeleteTransaction,
  } = useTransactionsPage()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold">{t('transactions')}</h1>
        </div>
        <Button onClick={() => setAddDialogOpen(true)}>
          <Plus className="mr-2 size-4" />
          {t('addTransaction')}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative min-w-0 flex-1 basis-full sm:basis-auto sm:max-w-sm">
          <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
          <Input
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter || 'all'} onValueChange={handleTypeFilterChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('allTypes')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allTypes')}</SelectItem>
            {TRANSACTION_TYPE_KEYS.map((type) => (
              <SelectItem key={type} value={type}>
                {t(type === 'INCOME' ? 'income' : 'expense')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={categoryFilter || 'all'} onValueChange={handleCategoryFilterChange}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder={t('allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allCategories')}</SelectItem>
            {CATEGORY_KEYS.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {t(`categories.${cat.toLowerCase()}` as 'categories.other')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <TableSkeleton rows={10} columns={6} />
      ) : isEmpty ? (
        <EmptyState
          icon={Receipt}
          title={t('noTransactions')}
          description={t('noTransactionsDescription')}
        />
      ) : (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('columns.description')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('columns.type')}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t('columns.category')}</TableHead>
                  <TableHead className="text-right">{t('columns.amount')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('columns.date')}</TableHead>
                  <TableHead className="hidden lg:table-cell">
                    {t('columns.paymentMethod')}
                  </TableHead>
                  <TableHead className="w-[90px]">{t('columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => (
                  <TransactionRow
                    key={tx.id}
                    tx={tx}
                    t={t}
                    onEdit={setEditTargetTransaction}
                    onDelete={setDeleteTargetId}
                  />
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
              <p className="text-muted-foreground text-sm">
                {t('pagination.showing', { count: total, page, totalPages })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="mr-1 size-4" />
                  {t('pagination.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  {t('pagination.next')}
                  <ChevronRight className="ml-1 size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      <AddTransactionDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} />

      <EditTransactionDialog
        transaction={editTargetTransaction}
        onClose={() => setEditTargetTransaction(null)}
      />

      <AlertDialog open={!!deleteTargetId} onOpenChange={() => setDeleteTargetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteTransaction')}</AlertDialogTitle>
            <AlertDialogDescription>{t('confirmDeleteTransaction')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTransaction}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

const TransactionRow = memo(function TransactionRow({
  tx,
  t,
  onEdit,
  onDelete,
}: {
  tx: FinancialTransaction
  t: ReturnType<typeof useTransactionsPage>['t']
  onEdit: (tx: FinancialTransaction) => void
  onDelete: (id: string) => void
}) {
  return (
    <TableRow>
      <TableCell className="max-w-[300px] truncate font-medium">{tx.description}</TableCell>
      <TableCell className="hidden md:table-cell">
        <Badge variant={typeBadgeVariant[tx.transactionType]}>
          {t(tx.transactionType === 'INCOME' ? 'income' : 'expense')}
        </Badge>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <Badge variant="secondary">
          {t(`categories.${tx.category.toLowerCase()}` as 'categories.other')}
        </Badge>
      </TableCell>
      <TableCell className="text-right font-mono">
        <span className={tx.transactionType === 'INCOME' ? 'text-green-600' : 'text-red-600'}>
          {tx.transactionType === 'INCOME' ? '+' : '-'}
          {formatAmountCents(tx.amountCents)}
        </span>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {new Date(tx.transactionDate).toLocaleDateString('bg-BG')}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        {t(`paymentMethods.${tx.paymentMethod.toLowerCase()}` as 'paymentMethods.other')}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="size-8" onClick={() => onEdit(tx)}>
            <Pencil className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-destructive hover:text-destructive"
            onClick={() => onDelete(tx.id)}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
})
